import { useState, useRef, useCallback } from 'react';
import Badge from '../common/Badge.jsx';

const GRIDS = {
  1: { cols: 1, rows: 1, label: '1×1' },
  4: { cols: 2, rows: 2, label: '2×2' },
  9: { cols: 3, rows: 3, label: '3×3' },
};

export default function GridLayout({ devices, socket, onSnapshot, onFullscreen }) {
  const [gridSize, setGridSize] = useState(1);
  const [fullscreenId, setFullscreenId] = useState(null);
  const videoRefs = useRef({});

  const { cols } = GRIDS[gridSize];
  const slots = Array.from({ length: gridSize }, (_, i) => devices[i] || null);

  const handleSnapshot = useCallback((deviceId) => {
    const video = videoRefs.current[deviceId];
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    onSnapshot?.(deviceId, dataUrl);
    const link = document.createElement('a');
    link.download = `elvyra_snapshot_${deviceId}_${Date.now()}.jpg`;
    link.href = dataUrl;
    link.click();
  }, [onSnapshot]);

  const toggleFullscreen = (deviceId) => {
    setFullscreenId(id => id === deviceId ? null : deviceId);
    onFullscreen?.(deviceId);
  };

  if (fullscreenId) {
    const device = devices.find(d => d.id === fullscreenId);
    return (
      <div style={{ position: 'relative', height: 'calc(100vh - 120px)', background: '#000', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <CameraCell
          device={device}
          socket={socket}
          videoRefs={videoRefs}
          onSnapshot={handleSnapshot}
          onFullscreen={toggleFullscreen}
          isFullscreen={true}
        />
        <button
          onClick={() => setFullscreenId(null)}
          style={{
            position: 'absolute', top: 12, right: 12, zIndex: 10,
            background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff', borderRadius: 'var(--radius-md)', padding: '6px 14px',
            cursor: 'pointer', fontSize: 13,
          }}>
          ⊠ Quitter plein écran
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Sélecteur de grille */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--elvyra-text-secondary)' }}>Vue :</span>
        {Object.entries(GRIDS).map(([size, cfg]) => (
          <button
            key={size}
            onClick={() => setGridSize(Number(size))}
            style={{
              padding: '4px 12px', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              fontSize: 'var(--text-xs)', fontWeight: 600,
              background: gridSize === Number(size) ? 'var(--elvyra-accent)' : 'var(--elvyra-surface-2)',
              color:      gridSize === Number(size) ? 'var(--elvyra-primary)' : 'var(--elvyra-text-secondary)',
              border: `1px solid ${gridSize === Number(size) ? 'var(--elvyra-accent)' : 'var(--elvyra-border)'}`,
            }}>
            {cfg.label}
          </button>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)' }}>
          {devices.filter(d => d.status === 'ONLINE').length}/{devices.length} en ligne
        </span>
      </div>

      {/* Grille */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: 8,
      }}>
        {slots.map((device, idx) => (
          <div key={device?.id || `empty-${idx}`}
            style={{ aspectRatio: '16/9', background: '#000', borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--elvyra-border)' }}>
            {device ? (
              <CameraCell
                device={device}
                socket={socket}
                videoRefs={videoRefs}
                onSnapshot={handleSnapshot}
                onFullscreen={toggleFullscreen}
                isFullscreen={false}
                compact={gridSize === 9}
              />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8 }}>
                <span style={{ fontSize: gridSize === 9 ? 24 : 36, opacity: 0.3 }}>📷</span>
                {gridSize !== 9 && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)' }}>Slot libre</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CameraCell({ device, socket, videoRefs, onSnapshot, onFullscreen, isFullscreen, compact }) {
  const [status, setStatus] = useState(device.status);
  const [motionAlert, setMotionAlert] = useState(false);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: '#000' }}>
      <video
        ref={el => { if (el) videoRefs.current[device.id] = el; }}
        autoPlay playsInline muted
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />

      {/* Overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 35%, transparent 65%, rgba(0,0,0,0.65) 100%)',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        padding: compact ? 6 : 10,
      }}>
        {/* Haut */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!compact && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
              {device.name}
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 20,
              background: status === 'ONLINE' ? 'rgba(0,200,150,0.25)' : 'rgba(107,114,128,0.25)',
              color: status === 'ONLINE' ? '#00C896' : '#6B7280',
            }}>
              {status === 'ONLINE' ? '● LIVE' : '○ OFF'}
            </span>
          </div>
        </div>

        {/* Bas */}
        {!compact && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {device.location && (
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.6)' }}>📍 {device.location}</span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
              <ActionBtn icon="📸" title="Snapshot" onClick={() => onSnapshot(device.id)} />
              <ActionBtn icon={isFullscreen ? '⊠' : '⤢'} title="Plein écran" onClick={() => onFullscreen(device.id)} />
            </div>
          </div>
        )}
      </div>

      {/* Motion alert border */}
      {motionAlert && (
        <div style={{ position: 'absolute', inset: 0, border: '2px solid var(--elvyra-danger)', borderRadius: 'var(--radius-md)', pointerEvents: 'none', animation: 'motionPulse 0.8s infinite' }} />
      )}
    </div>
  );
}

function ActionBtn({ icon, title, onClick }) {
  return (
    <button
      onClick={onClick} title={title}
      style={{
        background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 6, padding: '3px 7px', cursor: 'pointer', fontSize: 12, color: '#fff',
        transition: 'background 150ms',
      }}
      onMouseEnter={e => e.target.style.background = 'rgba(0,212,255,0.25)'}
      onMouseLeave={e => e.target.style.background = 'rgba(0,0,0,0.55)'}
    >
      {icon}
    </button>
  );
}
